import type { Thread } from '@atlas/api-contract';
import {
  MessageSquareDashed,
  PanelRightClose,
  PanelRightOpen,
} from 'lucide-react';

import { EmptyState, ErrorState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ViewerPanelFilters } from '@/features/viewer/viewer-store';
import type { ApiError } from '@/lib/api/errors';

import { ThreadPanelFilters } from './thread-panel-filters';
import { ThreadPanelRow } from './thread-panel-row';

export interface ThreadPanelProps {
  /** The rows to list, already filtered, newest first. */
  readonly threads: readonly Thread[];
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: ApiError | null;
  readonly onRetry: () => void;

  readonly filters: ViewerPanelFilters;
  readonly onFiltersChange: (patch: Partial<ViewerPanelFilters>) => void;
  readonly screenKnown: boolean;

  readonly collapsed: boolean;
  readonly onToggleCollapsed: () => void;

  readonly selectedThreadId: string | null;
  readonly highlightedThreadId: string | null;
  readonly onSelect: (thread: Thread) => void;
  readonly onHover: (threadId: string | null) => void;

  readonly basePath: string | null;
  readonly entryPath: string | null;
}

/** The panel's skeleton, shaped like the rows it will be replaced by. */
const ThreadPanelSkeleton = () => (
  <div role="status" aria-busy="true" aria-label="Loading comments" className="space-y-2">
    {[0, 1, 2, 3].map((row) => (
      <Card key={row} aria-hidden="true" className="shadow-none">
        <CardHeader className="flex-row items-center gap-2 space-y-0 p-2.5 pb-0">
          <Skeleton className="size-5 rounded-full" />
          <Skeleton className="h-3 w-24" />
          <Skeleton className="ml-auto h-3 w-14" />
        </CardHeader>
        <CardContent className="space-y-2 p-2.5 pt-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <div className="flex gap-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-14" />
          </div>
        </CardContent>
      </Card>
    ))}
    <span className="sr-only">Loading comments</span>
  </div>
);

/**
 * The collapsible right-hand panel (spec R6.1–R6.4).
 *
 * ============================================================================
 * WHY IT COLLAPSES TO A RAIL RATHER THAN DISAPPEARING
 * ============================================================================
 * The panel takes 20rem from a canvas that is already showing somebody else's
 * 1440-wide layout, so a reviewer checking a desktop breakpoint has to be able
 * to get rid of it. But a panel that vanished entirely would leave no evidence
 * that comments exist at all — and the one thing worse than a cramped canvas is
 * a reviewer who never finds the thread someone left for them. So it collapses
 * to a rail carrying the count, which is both the way back and the reminder.
 *
 * Collapse survives a version switch, because it describes how the reviewer is
 * working rather than what they are looking at.
 *
 * The three loading states are the app's shared ones (spec R6.4) — the same
 * skeleton geometry as the real rows so the list does not jump, the same
 * `ErrorState` with a retry, and an empty state that names the mechanism
 * ("comment mode") rather than just reporting the absence, because a reviewer
 * seeing an empty panel on their first visit needs to be told how to fill it.
 */
export const ThreadPanel = ({
  threads,
  isPending,
  isError,
  error,
  onRetry,
  filters,
  onFiltersChange,
  screenKnown,
  collapsed,
  onToggleCollapsed,
  selectedThreadId,
  highlightedThreadId,
  onSelect,
  onHover,
  basePath,
  entryPath,
}: ThreadPanelProps) => {
  if (collapsed) {
    return (
      <aside className="flex w-12 shrink-0 flex-col items-center gap-2 border-l border-viewer-chrome-border bg-viewer-chrome py-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 text-viewer-chrome-foreground hover:text-foreground"
              onClick={onToggleCollapsed}
            >
              <PanelRightOpen aria-hidden="true" className="size-4" />
              <span className="sr-only">Show the comments panel</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">Show comments</TooltipContent>
        </Tooltip>

        {isPending || isError ? null : (
          <span className="text-xs font-medium tabular-nums text-viewer-chrome-foreground">
            {threads.length}
          </span>
        )}
      </aside>
    );
  }

  return (
    <aside className="flex w-80 shrink-0 flex-col border-l border-viewer-chrome-border bg-background">
      <header className="flex shrink-0 items-center gap-2 border-b border-viewer-chrome-border px-3 py-2">
        <h2 className="text-sm font-medium text-foreground">Comments</h2>
        {isPending || isError ? null : (
          <span className="text-xs tabular-nums text-muted-foreground">
            {threads.length}
          </span>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-auto size-8 text-muted-foreground hover:text-foreground"
              onClick={onToggleCollapsed}
            >
              <PanelRightClose aria-hidden="true" className="size-4" />
              <span className="sr-only">Hide the comments panel</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">Hide comments</TooltipContent>
        </Tooltip>
      </header>

      <div className="shrink-0 border-b border-viewer-chrome-border px-3 py-2">
        <ThreadPanelFilters
          filters={filters}
          onChange={onFiltersChange}
          screenKnown={screenKnown}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {isError ? (
          <ErrorState
            title="Could not load the comments"
            error={error}
            onRetry={onRetry}
          />
        ) : isPending ? (
          <ThreadPanelSkeleton />
        ) : threads.length === 0 ? (
          <EmptyState
            icon={MessageSquareDashed}
            title={
              filters.status === 'open' && !filters.currentScreenOnly
                ? 'No comments on this version yet'
                : 'Nothing matches these filters'
            }
            description={
              filters.status === 'open' && !filters.currentScreenOnly
                ? 'Turn on comment mode in the toolbar, then click anything in the prototype to leave the first one.'
                : 'Widen the status filter, or turn off “This screen”, to see the rest of this version’s comments.'
            }
          />
        ) : (
          <ul className="space-y-2">
            {threads.map((thread) => (
              <li key={thread.id}>
                <ThreadPanelRow
                  thread={thread}
                  basePath={basePath}
                  entryPath={entryPath}
                  isSelected={thread.id === selectedThreadId}
                  isHighlighted={thread.id === highlightedThreadId}
                  onSelect={onSelect}
                  onHover={onHover}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
};
